<script setup lang="ts">
import type { CmsElementImageSlider } from "@shopware/composables";

import { Carousel, Slide, Navigation } from "vue3-carousel";

const props = defineProps<{
  content: CmsElementImageSlider;
}>();
const { data } = props.content;
const { Slides, toplineText, headlineText, toplineType, headlineType } = data;
const imageSlider = ref(null);
const currentSlide = ref(0);

const settings = {
  itemsToShow: 4,
  itemsToScroll: 4,
  wrapAround: false,
  snapAlign: "start",
  autoplay: 0,
  transition: 600,
  pauseAutoplayOnHover: true,
  breakpoints: {
    // 200px and up
    200: {
      itemsToShow: 1,
      itemsToScroll: 1,
      snapAlign: "center",
    },
    // 540 and up
    540: {
      itemsToShow: 2,
      itemsToScroll: 2,
      snapAlign: "start",
    },
    600: {
      itemsToShow: 3,
      itemsToScroll: 3,
      snapAlign: "start",
    },
    900: {
      itemsToShow: 4,
      itemsToScroll: 4,
      snapAlign: "start",
    },
  },
};
</script>
<template>
  <!-- need some with here for small views that the slider can calculate the correct items with -->
  <div class="cms-element-image-slider md:w-auto">
    <div class="container px-0 xl:px-20">
      <div class="max-w-[90rem] mx-auto mb-10">
        <SharedTopline :headline="toplineText" :headlineType="toplineType">
        </SharedTopline>
        <SharedHeadline :headline="headlineText" :headlineType="headlineType">
        </SharedHeadline>
      </div>
    </div>
    <div class="container px-0 xl:px-20">
      <div class="desktop:-mx-10">
        <Carousel v-bind="settings" ref="imageSlider" v-model="currentSlide">
          <template #slides>
            <Slide v-for="slide of Slides" :key="slide">
              <SharedLink :button="slide.slideLink" class="w-full">
                <template #link>
                  <div class="slide text-center px-0 sm:px-2.5 w-full group">
                    <div
                      class="h-[21.25rem] text-skin-white flex items-center justify-center rounded overflow-hidden"
                    >
                      <NuxtImg
                        class="-z-[2] w-auto h-full object-contain top-0 left-0 h-[14.25rem]"
                        v-if="slide.slideImage && slide.slideImage.media"
                        loading="lazy"
                        format="webp"
                        :src="slide.slideImage?.media?.url"
                      />
                    </div>
                    <div
                      class="w-full h-full flex px-5 flex-col items-center justify-center"
                    >
                      <h3 class="text-base font-bold mt-2">
                        {{ slide.slideTitle }}
                      </h3>
                    </div>
                  </div>
                </template>
              </SharedLink>
            </Slide>
          </template>
          <template #addons>
            <div
              class="carousel__navigation-button pointer-events-none z-0 absolute bottom-0 md:bottom-auto top-1/4 md:top-2/4 max-w-[90rem] flex justify-between desktop:-left-[60px] desktop:-right-[60px] -left-5 -right-5 mx-auto px-3 laptop:px-10 [&>button]:static"
            >
              <Navigation>
                <template #next>
                  <img
                    src="assets/icons/slide-right.svg"
                    alt="Next"
                    class="w-6 h-6"
                  />
                </template>
                <template #prev>
                  <img
                    src="assets/icons/slide-left.svg"
                    alt="Previous"
                    class="w-6 h-6"
                  />
                </template>
              </Navigation>
            </div>
          </template>
        </Carousel>
      </div>
    </div>
  </div>
</template>
<style scoped>
.carousel__navigation-button :deep(.carousel__prev),
.carousel__navigation-button :deep(.carousel__next) {
  @apply bg-skin-icon text-skin-white w-12 h-12 rounded-full flex items-center justify-center;
  position: relative;
  pointer-events: all;
}
</style>
